import { Gift } from "lucide-react";
import { LANGUAGES, SupportedLanguage } from "../../../services/realtimeVoice";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";

interface HeaderProps {
    language: SupportedLanguage;
    showLiveCaptions: boolean;
    setShowLiveCaptions: (show: boolean) => void;
    isPremium: boolean;
    hasAddonBalance: boolean;
    selectedPersonaId: string;
    onExit: () => void;
    onShowPersonaSheet: () => void;
    showExitAndLanguage?: boolean;
}

export function Header({
    language,
    showLiveCaptions,
    setShowLiveCaptions,
    isPremium,
    hasAddonBalance,
    selectedPersonaId,
    onExit,
    onShowPersonaSheet,
    showExitAndLanguage = true
}: HeaderProps) {
    const { theme, t } = useAppSettings();
    const isDark = theme === 'dark';

    return (
        <div className="absolute left-0 right-0 z-50 flex items-center justify-between px-4 pointer-events-none" style={{ top: '10vh' }}>

            {/* Left Group: Exit Button + Language Label */}
            {showExitAndLanguage ? (
                <div className="flex items-center gap-4 pointer-events-auto">
                    {/* 退出按钮 */}
                    <button
                        onClick={onExit}
                        className={`flex items-center justify-center w-10 h-10 rounded-full border backdrop-blur-sm transition-all ${isDark
                            ? 'bg-white/10 border-white/30 text-white/80 hover:bg-white/20 hover:text-white'
                            : 'bg-black/10 border-white/50 text-gray-700/80 hover:bg-white/40 hover:text-gray-900'
                            }`}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    {/* 语言标签 */}
                    <div className={`flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm rounded-full border ${isDark ? 'bg-white/10 border-white/20' : 'bg-white/40 border-white/50'
                        }`}>
                        <span className={`text-xs ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                            {LANGUAGES[language]?.name || language}
                        </span>
                        {hasAddonBalance && (
                            <Gift className="w-3 h-3 text-orange-400 animate-pulse" />
                        )}
                        {isPremium && !hasAddonBalance && (
                            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                        )}
                    </div>
                </div>
            ) : (
                <div /> // 占位符，保持布局
            )}

            {/* Right Group: Control Buttons */}
            <div className="flex items-center gap-3 pointer-events-auto">
                {/* CC字幕开关按钮 */}
                <button
                    type="button"
                    onClick={() => setShowLiveCaptions(!showLiveCaptions)}
                    aria-label={showLiveCaptions ? t('whisper.cc.turnOff') : t('whisper.cc.turnOn')}
                    className={`relative flex items-center justify-center w-12 h-12 rounded-xl border transition-all ${showLiveCaptions
                        ? isDark
                            ? 'bg-white/30 border-white/70 text-[#02082f] shadow-[0_12px_30px_rgba(0,0,0,0.25)]'
                            : 'bg-white/60 border-white/80 text-gray-800 shadow-[0_12px_30px_rgba(0,0,0,0.15)]'
                        : isDark
                            ? 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                            : 'bg-white/30 border-white/50 text-gray-700 hover:bg-white/40'
                        }`}
                >
                    <span className="text-sm font-semibold tracking-wide">CC</span>
                </button>

                {/* AI性格选择按钮 */}
                <button
                    type="button"
                    onClick={onShowPersonaSheet}
                    aria-label={t('whisper.persona.button')}
                    className={`relative flex items-center justify-center w-12 h-12 rounded-xl border transition-all ${isDark
                        ? 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                        : 'bg-white/30 border-white/50 text-gray-700 hover:bg-white/40'
                        }`}
                >
                    {isDark ? (
                        <img src="/images/svg/character.svg" alt="AI Character" className="w-5 h-5" />
                    ) : (
                        <div
                            className="w-5 h-5"
                            style={{
                                maskImage: `url(/images/svg/character.svg)`,
                                maskSize: 'contain',
                                maskRepeat: 'no-repeat',
                                maskPosition: 'center',
                                backgroundColor: '#587FFF',
                                WebkitMaskImage: `url(/images/svg/character.svg)`,
                                WebkitMaskSize: 'contain',
                                WebkitMaskRepeat: 'no-repeat',
                                WebkitMaskPosition: 'center',
                            }}
                        />
                    )}
                    {/* 如果选择的不是默认性格，显示黄色指示点 */}
                    {selectedPersonaId !== 'listener' && (
                        <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${isDark
                            ? 'bg-yellow-300 shadow-[0_0_10px_rgba(250,204,21,0.8)]'
                            : 'bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.6)]'
                            }`} />
                    )}
                </button>
            </div>
        </div>
    );
}
